import React from "react";
import { useTranslation } from "react-i18next";
import SplitDiscountRows from "./SplitDiscountRows.tsx";
import { getEqualSplitDistribution } from "../utils/splitCalc.ts";
import CheckoutFieldRow from "../ui/CheckoutFieldRow.tsx";
import CheckoutMoneyInput from "../ui/CheckoutMoneyInput.tsx";
import CheckoutSegmentedTabs from "../ui/CheckoutSegmentedTabs.tsx";
import type { useSplitPayment } from "../hooks/useSplitPayment";
import { showPrice } from "../../../../../../../utils/commonfunction.js";

interface SplitByAmountProps {
  splitPayment: ReturnType<typeof useSplitPayment>;
}

const SplitByAmount: React.FC<SplitByAmountProps> = ({ splitPayment }) => {
  const { t } = useTranslation();

  const { splitAmount } = splitPayment;
  const {
    splitAmountMode,
    splitAmountInput,
    splitPeopleCount,
    splitAmountWarning,
    isManualModeDisabled = false,
    isEqualModeDisabled = false,
    remainingAmount,
    remainingAfterSplit,
    setSplitAmountMode,
    setSplitAmountInput,
    setSplitPeopleCount,
  } = splitAmount;

  const peopleCount = Math.max(1, Number(splitPeopleCount) || 1);
  const equalSplitDistribution = getEqualSplitDistribution(remainingAmount, peopleCount);

  const translate = (key: string, fallback: string, options?: Record<string, string>) =>
    t(key, options) || fallback;

  const peopleSummaryLabel = (count: number) =>
    translate(
      count === 1 ? "label_equal_split_person_count" : "label_equal_split_people_count",
      count === 1 ? `${count} person` : `${count} people`,
      { count: String(count) }
    );

  const paySummaryLabel = (count: number) =>
    translate(
      count === 1 ? "label_equal_split_pays" : "label_equal_split_pay",
      count === 1 ? "pays" : "pay"
    );

  return (
    <>
      <SplitDiscountRows splitPayment={splitPayment} />
      <div className="split-sidebar-section amount-split-panel">
        <CheckoutSegmentedTabs
          className="split-subtabs compact-split-tabs"
          buttonClassName="split-subtab"
          value={splitAmountMode}
          onChange={setSplitAmountMode}
          options={[
            {
              value: "manual",
              label: t("label_manual"),
              disabled: isManualModeDisabled,
            },
            {
              value: "equal",
              label: t("label_equal_split"),
              disabled: isEqualModeDisabled,
            },
          ]}
        />

        {splitAmountMode === "manual" ? (
          <CheckoutFieldRow label={t("label_split_amount")} error={splitAmountWarning}>
            <CheckoutMoneyInput
              type="number"
              min={1}
              value={splitAmountInput}
              onChange={setSplitAmountInput}
              placeholder={t("enter_gift_card_amount")}
              className="input-group"
            />
          </CheckoutFieldRow>
        ) : (
          <>
            <div className="amount-people-row">
              <span>{t("label_number_of_people")}</span>

              <div className="amount-people-stepper">
                <button
                  type="button"
                  onClick={() =>
                    setSplitPeopleCount(String(Math.max(2, peopleCount - 1)))
                  }
                >
                  -
                </button>

                <input
                  type="number"
                  min="2"
                  value={splitPeopleCount}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setSplitPeopleCount(event.target.value)
                  }
                />

                <button type="button" onClick={() => setSplitPeopleCount(String(peopleCount + 1))}>
                  +
                </button>
              </div>
            </div>

            <div className="equal-split-breakdown">
              <div className="equal-split-summary-card">
                {equalSplitDistribution.dividesEvenly ? (
                  <div className="equal-split-summary-panel equal-split-summary-panel-full">
                    <span className="equal-split-summary-label">
                      {translate("label_each_person_pays", "Each person pays")}
                    </span>
                    <strong className="equal-split-summary-amount">
                      ¥{showPrice(equalSplitDistribution.currentShare)}
                    </strong>
                  </div>
                ) : (
                  <>
                    <div className="equal-split-summary-panel">
                      <span className="equal-split-summary-label">
                        {peopleSummaryLabel(equalSplitDistribution.higherShareCount)}{" "}
                        {paySummaryLabel(equalSplitDistribution.higherShareCount)}
                      </span>
                      <strong className="equal-split-summary-amount">
                        ¥{showPrice(equalSplitDistribution.higherShare)}
                      </strong>
                    </div>
                    <div className="equal-split-summary-divider" />
                    <div className="equal-split-summary-panel align-end">
                      <span className="equal-split-summary-label">
                        {peopleSummaryLabel(equalSplitDistribution.lowerShareCount)}{" "}
                        {paySummaryLabel(equalSplitDistribution.lowerShareCount)}
                      </span>
                      <strong className="equal-split-summary-amount">
                        ¥{showPrice(equalSplitDistribution.lowerShare)}
                      </strong>
                    </div>
                  </>
                )}
              </div>
              <div
                className={`equal-split-note ${
                  equalSplitDistribution.dividesEvenly ? "is-even" : "is-uneven"
                }`}
              >
                <span className="equal-split-note-icon">
                  {equalSplitDistribution.dividesEvenly ? "✓" : "△"}
                </span>
                <span className="equal-split-note-text">
                  {equalSplitDistribution.dividesEvenly
                    ? translate(
                        "label_equal_split_even_note",
                        `¥${showPrice(
                          remainingAmount
                        )} divides evenly among ${peopleCount} people — no rounding needed.`,
                        {
                          amount: showPrice(remainingAmount),
                          count: String(peopleCount),
                        }
                      )
                    : translate(
                        "label_equal_split_rounding_note",
                        `¥${showPrice(
                          remainingAmount
                        )} can't be split equally. ${
                          equalSplitDistribution.higherShareCount
                        } people pay ¥1 extra to cover the difference.`,
                        {
                          amount: showPrice(remainingAmount),
                          higherCount: String(equalSplitDistribution.higherShareCount),
                        }
                      )}
                </span>
              </div>
            </div>
          </>
        )}
        <p className="split-muted-text text-end">
          {t("label_remaining_after_this_split", {
            amount: showPrice(remainingAfterSplit),
          })}
        </p>
      </div>
    </>
  );
};

export default SplitByAmount;
